import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  ButtonGroup,
  Container,
  Flex,
  Heading,
  VStack,
} from '@chakra-ui/react';
import { ApplicationState } from '../../../../../store';
import {
  Combobox,
  DateInput,
  Input,
  IOption,
  PhoneNumberInput,
  RadioOptions,
  RadioOptionsWithConfirm,
} from 'shared-components';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { Agent, FeeStatus, ApplicantDetailsSchema, IApplicantData } from 'api-paths-cas';
import { countries } from 'platform-data';
import { HIGHEST_QUALIFICATIONS_LIST } from 'apply-data';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { updateApplicantDetails } from '@/store/apply/actions';
import { getAgentAliases } from '@/store/agency/actions';
import { ApplyActionTypes } from '@/store/apply/types';
import { feeStatusLabels, InputChangeEvent } from './helper';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';

export default function ApplicantDetails(): React.JSX.Element {
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();
  const HIGHEST_QUALIFICATIONS_NAMES: Array<IOption> = [...HIGHEST_QUALIFICATIONS_LIST];
  const { offerId, userId, uni } = params;
  const { application, isUpdatingApplicantDetails, applicantUpdateSuccess, updatedApplicationUserId } = useSelector(
    (state: ApplicationState) => state.apply,
  );
  const { mappedAgentAliases } = useSelector((state: ApplicationState) => state.agencies);
  const { agentId } = useSelector((state: ApplicationState) => state.apply.application);
  const [phoneValidationError, setPhoneValidationError] = useState<string>('');

  const defaultValues = useMemo(
    () => ({
      givenName: application?.givenName || '',
      middleName: application?.middleName || '',
      familyName: application?.familyName || '',
      email: application?.email || '',
      phoneNumber: application?.phoneNumber || '',
      nationality: application?.nationality || '',
      feeStatus: application?.feeStatus || FeeStatus.NotSure,
      highestQualificationCode: application?.highestQualification?.code || '',
      agentId: agentId || Agent.None,
      isVisaRequired: application?.isVisaRequired,
      dob: application.dob,
    }),
    [application, agentId],
  );

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
    reset,
  } = useForm<IApplicantData>({
    resolver: zodResolver(ApplicantDetailsSchema),
    defaultValues,
    mode: 'onBlur',
  });

  const watchedValues = watch();
  const originalValues = defaultValues;

  useEffect(() => {
    dispatch(getAgentAliases());
  }, [dispatch]);

  const nationalityOptions = useMemo(() => {
    return countries
      .map(country => ({
        value: country.nationality,
        label: country.nationality,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  const agents: IOption[] = useMemo(() => {
    const agentOptions =
      mappedAgentAliases.items?.map(agent => ({
        label: agent.assignedAgent.displayName,
        value: agent.assignedAgent.id,
      })) ?? [];
    return [...agentOptions, { label: 'No agent', value: Agent.None }];
  }, [mappedAgentAliases]);

  const isVisaStatusVisible = [FeeStatus.Overseas, FeeStatus.NotSure].includes(watchedValues.feeStatus);

  const handlePhoneCodeChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newCode = event.currentTarget.value;
    const currentPhone = watchedValues.phoneNumber || '';
    const phoneNumber = currentPhone.split('-')[1] || '';
    const newPhoneNumber = newCode ? `${newCode}-${phoneNumber}` : `-${phoneNumber}`;
    setValue('phoneNumber', newPhoneNumber, { shouldDirty: true });
  };

  const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newPhoneNumber = event.currentTarget.value;
    const currentPhone = watchedValues.phoneNumber || '';
    const code = currentPhone.split('-')[0] || '';
    const fullPhoneNumber = code ? `${code}-${newPhoneNumber}` : `-${newPhoneNumber}`;
    setValue('phoneNumber', fullPhoneNumber, { shouldDirty: true });
  };

  const handlePhoneValidationFailed = (): void => {
    setPhoneValidationError('Phone number is invalid');
  };

  const handlePhoneValidationSuccess = (): void => {
    setPhoneValidationError('');
  };

  const onSubmit = async (data: IApplicantData): Promise<void> => {
    if (phoneValidationError || isUpdatingApplicantDetails) {
      return;
    }

    try {
      dispatch(
        updateApplicantDetails({
          applicantId: userId,
          uni,
          offerId,
          applicationId: application.applicationId,
          data: {
            ...data,
            email: data.email.toLowerCase(),
            isVisaRequired: isVisaStatusVisible ? data.isVisaRequired : undefined,
          },
        }),
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to update applicant details', error);
      toast.error('Failed to update applicant details. Please try again.');
    }
  };

  useEffect(() => {
    if (applicantUpdateSuccess && updatedApplicationUserId) {
      navigate(`/application/${uni}/${updatedApplicationUserId}/${application.applicationId}/summary`, {
        replace: true,
      });
      dispatch({ type: ApplyActionTypes.UPDATE_APPLICANT_DETAILS_RESET });
    }
  }, [applicantUpdateSuccess, updatedApplicationUserId, navigate, uni, application.applicationId, dispatch]);

  const handleCancel = (): void => {
    reset(defaultValues);
    setPhoneValidationError('');
    navigate(-1);
  };

  return (
    <Box minH="100vh" p={8} data-selector="applicantDetailsEditPage">
      <Container maxW="2xl">
        <Box ml={-3.5}>
          <Button
            id="back-to"
            data-selector="back-to"
            size="md"
            variant="ghost"
            leftIcon={<FontAwesomeIcon icon={faArrowLeft} className="utility-icon" />}
            as={Link}
            to="../summary"
          >
            Return
          </Button>
        </Box>
        <Heading mb={8}>Edit applicant details</Heading>
        <Box as="form" onSubmit={handleSubmit(onSubmit)}>
          <VStack spacing={6} align="stretch">
            <Controller
              name="givenName"
              control={control}
              render={({ field }) => (
                <Input
                  id="givenName"
                  name="givenName"
                  label="Given name"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  errorText={errors.givenName?.message}
                  inputProps={{
                    autoComplete: 'given-name',
                  }}
                />
              )}
            />
            <Controller
              name="middleName"
              control={control}
              render={({ field }) => (
                <Input
                  id="middleName"
                  name="middleName"
                  label="Middle name (Optional)"
                  value={field.value || ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  inputProps={{
                    autoComplete: 'additional-name',
                  }}
                />
              )}
            />
            <Controller
              name="familyName"
              control={control}
              render={({ field }) => (
                <Input
                  id="familyName"
                  name="familyName"
                  label="Family name"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  errorText={errors.familyName?.message}
                  inputProps={{
                    autoComplete: 'family-name',
                  }}
                />
              )}
            />
            <Box>
              <Controller
                name="feeStatus"
                control={control}
                render={({ field }) => (
                  <RadioOptions
                    id="fee_status_change"
                    label="Fee status"
                    mb={2}
                    questions={[
                      {
                        char: '1',
                        label: feeStatusLabels[FeeStatus.Home],
                        selected: field.value === FeeStatus.Home,
                      },
                      {
                        char: '2',
                        label: feeStatusLabels[FeeStatus.Overseas],
                        selected: field.value === FeeStatus.Overseas,
                      },
                      {
                        char: '3',
                        label: feeStatusLabels[FeeStatus.NotSure],
                        selected: field.value === FeeStatus.NotSure,
                      },
                    ]}
                    onClick={option => {
                      const newFeeStatus = ((): string => {
                        if (option.char === '1') return FeeStatus.Home;
                        if (option.char === '2') return FeeStatus.Overseas;
                        if (option.char === '3') return FeeStatus.NotSure;
                        return field.value;
                      })();
                      field.onChange(newFeeStatus);
                    }}
                    errorText={errors?.feeStatus?.message}
                  />
                )}
              />
              {watchedValues.feeStatus !== originalValues.feeStatus && !errors.feeStatus && (
                <Alert status="warning" role="fee status" mt={2}>
                  <AlertIcon />
                  <AlertTitle color="body-text" fontWeight="normal">
                    Existing offers will not be changed. Offer a new programme to use the requirements for this fee
                    status.
                  </AlertTitle>
                </Alert>
              )}
            </Box>
            <Controller
              name="highestQualificationCode"
              control={control}
              render={({ field }) => (
                <Combobox
                  id="highest_qualification_change"
                  label="Highest qualification on entry"
                  options={HIGHEST_QUALIFICATIONS_NAMES}
                  value={[field.value]}
                  onChange={({ value }: IOption) => field.onChange(value)}
                  size="lg"
                />
              )}
            />

            <Controller
              name="agentId"
              control={control}
              render={({ field }) => (
                <Combobox
                  label="Agent"
                  id="agent"
                  options={agents}
                  onChange={(agent: IOption) => field.onChange(agent.value)}
                  value={[field.value]}
                />
              )}
            />

            {isVisaStatusVisible && (
              <Controller
                name="isVisaRequired"
                control={control}
                render={({ field }) => (
                  <RadioOptionsWithConfirm
                    id="visa_required_change"
                    label="Student visa"
                    mb={2}
                    questions={[
                      {
                        char: '1',
                        label: 'Visa not required',
                        selected: field.value === false,
                      },
                      {
                        char: '2',
                        label: 'Visa required',
                        selected: field.value === true,
                      },
                    ]}
                    onClick={option => field.onChange(option.char !== '1')}
                  />
                )}
              />
            )}
            <Controller
              name="nationality"
              control={control}
              render={({ field }) => (
                <Combobox
                  label="Nationality"
                  id="nationality"
                  options={nationalityOptions}
                  value={[field.value]}
                  onChange={(val: IOption) => field.onChange(val.value)}
                  placeholder="Select nationality"
                  size="lg"
                />
              )}
            />
            <Controller
              control={control}
              name="dob"
              render={({ field: { onChange, ref, value = '' }, fieldState: { error } }) => {
                const [YYYY, MM, DD] = value.split('-');

                return (
                  <DateInput
                    forwardedRef={ref}
                    key="dob"
                    id="dob"
                    name="dob"
                    label="Date of birth"
                    dayName="dobDay"
                    monthName="dobMonth"
                    yearName="dobYear"
                    value={{ DD, MM, YYYY }}
                    errorText={error?.message}
                    onChangeDD={(evt: InputChangeEvent) => {
                      const newDD = evt.currentTarget.value;
                      const newValue = [YYYY, MM, newDD].join('-');
                      onChange(newValue);
                    }}
                    onChangeMM={(evt: InputChangeEvent) => {
                      const newMM = evt.currentTarget.value;
                      const newValue = [YYYY, newMM, DD].join('-');
                      onChange(newValue);
                    }}
                    onChangeYY={(evt: InputChangeEvent) => {
                      const newYYYY = evt.currentTarget.value;
                      const newValue = [newYYYY, MM, DD].join('-');
                      onChange(newValue);
                    }}
                    inputPropsDD={{ autoComplete: 'bday-day' }}
                    inputPropsMM={{ autoComplete: 'bday-month' }}
                    inputPropsYY={{ autoComplete: 'bday-year' }}
                  />
                );
              }}
            />
            <Box>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <Input
                    label="Email address"
                    id="email"
                    name="email"
                    type="email"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    errorText={errors.email?.message}
                  />
                )}
              />
              {watchedValues.email !== originalValues.email && !errors.email && (
                <Alert status="warning" role="email address" mt={2} data-selector="email-warning">
                  <AlertIcon />
                  <AlertTitle color="body-text" fontWeight="normal">
                    Changing this will require the applicant to use the new email address to log in.
                  </AlertTitle>
                </Alert>
              )}
            </Box>
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field }) => (
                <PhoneNumberInput
                  id="phone-number"
                  label="Phone number"
                  codeValue={field.value ? field.value.split('-')[0] || '' : ''}
                  numberValue={field.value ? field.value.split('-')[1] || '' : ''}
                  errorTextCode={phoneValidationError || errors.phoneNumber?.message}
                  errorTextNumber={phoneValidationError || errors.phoneNumber?.message}
                  onValidationFailed={handlePhoneValidationFailed}
                  onValidationSuccess={handlePhoneValidationSuccess}
                  onChangeCode={handlePhoneCodeChange}
                  onChangeNumber={handlePhoneNumberChange}
                />
              )}
            />
            <ButtonGroup size="lg" w="full" spacing={4}>
              <Flex gap={4} w="full">
                <Button flex={1} onClick={handleCancel} isDisabled={isUpdatingApplicantDetails}>
                  Cancel
                </Button>
                <Button
                  flex={1}
                  variant="solid"
                  type="submit"
                  isDisabled={!isDirty || isUpdatingApplicantDetails || !!phoneValidationError}
                  isLoading={isUpdatingApplicantDetails}
                  loadingText="Saving..."
                  colorScheme="blue"
                  data-selector="save-edit-button"
                >
                  Save changes
                </Button>
              </Flex>
            </ButtonGroup>
          </VStack>
        </Box>
      </Container>
    </Box>
  );
}
